<script setup lang="ts">
import {
  HomeKpiGrid,
  HomeQuickLinks,
  TraceIdAlert,
  can,
  useSession,
  type KpiCard,
  type QuickLink,
} from '@yc/ui-kit'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { getHomeStats, type HomeStats } from '../api/home'
import { SCENARIO_LABELS, SCENARIOS, STATUS_LABELS } from '../api/simulations'

const { me } = useSession()
const router = useRouter()

const loading = ref(false)
const error = ref<unknown>(null)

const greeting = () => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
}

/** 卡片标签：后端 JSON 字段（internal/home/service.go 的 Stats）→ 页面文案 */
const defs: Array<{ key: keyof HomeStats; label: string }> = [
  { key: 'total_sites', label: '网点档案' },
  { key: 'active_sites', label: '启用网点' },
  { key: 'total_lanes', label: '路径档案' },
  { key: 'active_lanes', label: '启用路径' },
  { key: 'total_simulations', label: '仿真任务' },
  { key: 'draft', label: STATUS_LABELS.draft },
  { key: 'running', label: STATUS_LABELS.running },
  { key: 'done', label: STATUS_LABELS.done },
  { key: 'failed', label: STATUS_LABELS.failed },
]

/** 仿真任务按状态钻取：点卡片直接带着状态筛到列表 */
const simStatusByKey: Partial<Record<keyof HomeStats, string>> = {
  draft: 'draft',
  running: 'running',
  done: 'done',
  failed: 'failed',
}

const cards = ref<KpiCard[]>(defs.map((d) => ({ key: d.key, label: d.label, value: '—' })))

const links: QuickLink[] = [
  { to: '/sites', label: '网点布局', perm: 'nos:site' },
  { to: '/lanes', label: '运输路径', perm: 'nos:lane' },
  { to: '/simulations', label: '仿真推演', perm: 'nos:sim' },
]

function simStatus(card: KpiCard): string {
  return simStatusByKey[card.key as keyof HomeStats] ?? ''
}

onMounted(async () => {
  loading.value = true
  error.value = null
  try {
    const s = await getHomeStats()
    cards.value = defs.map((d) => ({ key: d.key, label: d.label, value: s[d.key] ?? '—' }))
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="yc-home">
    <header class="yc-home__hero">
      <h2 class="yc-home__greeting">{{ greeting() }}，{{ me?.name || me?.username }}</h2>
      <p class="yc-home__sub">
        {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
      </p>
    </header>

    <div v-if="error" class="yc-home__error">
      <TraceIdAlert :error="error" />
    </div>

    <HomeKpiGrid :cards="cards" :loading="loading">
      <template #extra="{ card }">
        <router-link
          v-if="can('nos:sim') && simStatus(card)"
          class="yc-home__drill"
          :to="{ path: '/simulations', query: { status: simStatus(card) } }"
        >
          查看
        </router-link>
      </template>
    </HomeKpiGrid>

    <HomeQuickLinks :links="links" @navigate="router.push" />

    <el-card>
      <template #header>
        <b>三种仿真场景</b>
      </template>
      <ul class="yc-home__scenes">
        <li v-for="s in SCENARIOS" :key="s">
          <b>{{ SCENARIO_LABELS[s] }}</b>
          <span>{{
            s === 'lane_optimize'
              ? '对参与网点按里程求最小生成树，输出总里程 / 估算成本 / 边列表。'
              : s === 'cost_compare'
                ? '对比现网成本与启用候选路径后的成本，给出节省额与节省率。'
                : '按城市统计能力与需求，给出覆盖率与瓶颈网点（负载 > 1 判 warn）。'
          }}</span>
        </li>
      </ul>
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  // 插槽内容由本组件渲染，yc-kpi-grid__card 是 position:relative 的定位上下文，
  // 这里的绝对定位以卡片右上角为基准，与改造前一致。
  &__drill {
    position: absolute;
    top: var(--yc-space-2);
    right: var(--yc-space-3);
    font-size: var(--yc-font-xs);
  }

  &__scenes {
    margin: 0;
    padding-left: var(--yc-space-5);
    font-size: var(--yc-font-sm);
    line-height: 1.9;

    b {
      margin-right: var(--yc-space-2);
    }

    span {
      color: var(--yc-text-secondary);
    }
  }
}
</style>
